import { http } from '@/lib/http';
import type {
  ProcurementAttachment,
  ProcurementDocKind,
  ProcurementReceipt,
  ProcurementSourcing,
  PurchaseRequest,
  PurchaseRequestStatus,
} from '@/types/procurement';

const PR_BASE = '/api/v1/procurement/purchase-requests';
const RCPT_BASE = '/api/v1/procurement/receipts';

// ── Purchase Requests ──────────────────────────────────────────────────

export interface PurchaseRequestListQuery {
  status?: PurchaseRequestStatus;
  sourcingType?: ProcurementSourcing;
  proposedVendorId?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface PurchaseRequestListResponse {
  rows: PurchaseRequest[];
  page: number;
  pageSize: number;
  total: number;
}

export interface PurchaseRequestLinePayload {
  itemId: string;
  quantity: number;
  estimatedUnitPrice?: number;
  lineNote?: string;
}

export interface CreatePurchaseRequestPayload {
  warehouseId: string;
  sourcingType: ProcurementSourcing;
  proposedVendorId?: string;
  departmentId?: string;
  items: PurchaseRequestLinePayload[];
  neededBy?: string;
  justification?: string;
  notes?: string;
}

export interface ApprovePurchaseRequestPayload {
  sourcingType?: ProcurementSourcing;
  proposedVendorId?: string;
  reviewerNote?: string;
}

export interface ConvertToPoPayload {
  vendorId?: string;
  expectedDeliveryAt?: string;
  notes?: string;
}

// ── Goods Receipts (GRN) ───────────────────────────────────────────────

export interface ReceiptListQuery {
  purchaseRequestId?: string;
  purchaseOrderId?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface ReceiptListResponse {
  rows: ProcurementReceipt[];
  page: number;
  pageSize: number;
  total: number;
}

export interface ReceiptLinePayload {
  itemId: string;
  quantityReceived: number;
  purchaseRequestItemId?: string;
  purchaseOrderItemId?: string;
}

export interface CreateReceiptPayload {
  purchaseRequestId?: string;
  purchaseOrderId?: string;
  warehouseId?: string;
  vendorId?: string;
  lines: ReceiptLinePayload[];
  supplierInvoiceNumber?: string;
  invoiceDate?: string;
  challanNumber?: string;
  ewayBillNumber?: string;
  actualTotal?: number;
  notes?: string;
  attachmentIds?: string[];
}

export const procurementService = {
  // Purchase requests
  listRequests: (query: PurchaseRequestListQuery = {}) =>
    http.get<PurchaseRequestListResponse>(PR_BASE, { params: query }),
  getRequest: (id: string) => http.get<PurchaseRequest>(`${PR_BASE}/${id}`),
  createRequest: (payload: CreatePurchaseRequestPayload) =>
    http.post<PurchaseRequest, CreatePurchaseRequestPayload>(PR_BASE, payload),
  submitRequest: (id: string) =>
    http.patch<PurchaseRequest, Record<string, never>>(`${PR_BASE}/${id}/submit`, {}),
  approveRequest: (id: string, payload: ApprovePurchaseRequestPayload = {}) =>
    http.patch<PurchaseRequest, ApprovePurchaseRequestPayload>(
      `${PR_BASE}/${id}/approve`,
      payload,
    ),
  rejectRequest: (id: string, reviewerNote: string) =>
    http.patch<PurchaseRequest, { reviewerNote: string }>(`${PR_BASE}/${id}/reject`, {
      reviewerNote,
    }),
  convertToPo: (id: string, payload: ConvertToPoPayload = {}) =>
    http.post<PurchaseRequest, ConvertToPoPayload>(`${PR_BASE}/${id}/convert-to-po`, payload),

  // Goods receipts
  listReceipts: (query: ReceiptListQuery = {}) =>
    http.get<ReceiptListResponse>(RCPT_BASE, { params: query }),
  getReceipt: (id: string) => http.get<ProcurementReceipt>(`${RCPT_BASE}/${id}`),
  createReceipt: (payload: CreateReceiptPayload) =>
    http.post<ProcurementReceipt, CreateReceiptPayload>(RCPT_BASE, payload),

  // Attachments (multipart). Optionally pre-linked to a receipt / PR.
  uploadAttachment: (args: {
    file: File;
    docKind: ProcurementDocKind;
    receiptId?: string;
    purchaseRequestId?: string;
  }) => {
    const form = new FormData();
    form.append('file', args.file);
    form.append('docKind', args.docKind);
    if (args.receiptId) form.append('receiptId', args.receiptId);
    if (args.purchaseRequestId) form.append('purchaseRequestId', args.purchaseRequestId);
    return http.post<ProcurementAttachment & { url: string }, FormData>(
      `${RCPT_BASE}/attachments`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
  },
  deleteAttachment: (id: string) =>
    http.delete<{ id: string; deleted: boolean }>(`${RCPT_BASE}/attachments/${id}`),
};
